import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ListChecks, ChevronDown, ChevronUp, CheckCircle2, Circle, Camera, Loader2, FileText, CalendarPlus, Zap } from "lucide-react";
import { api, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const src = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;

const PhotoPick = ({ item, kind, onChange }) => {
  const [busy, setBusy] = useState(false);
  const field = kind === "antes" ? "before_photo_path" : "after_photo_path";
  const path = item[field];
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.patch(`/fdp-list/${item.id}`, { [field]: up.data.path });
      onChange(r.data || { ...item, [field]: up.data.path });
      toast.success(`Foto ${kind} salva`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar foto");
    } finally { setBusy(false); }
  };
  return (
    <label className="relative w-14 h-14 rounded-lg border border-dashed border-slate-300 bg-white flex items-center justify-center cursor-pointer overflow-hidden shrink-0" data-testid={`fdpl-photo-${kind}-${item.id}`}>
      <input type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} disabled={busy || item.status === "convertido"} />
      {busy ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : path ? <img src={src(path)} alt="" className="w-full h-full object-cover" /> : <Camera className="w-4 h-4 text-slate-400" />}
      <span className="absolute bottom-0 inset-x-0 text-[8px] font-black uppercase text-center bg-black/55 text-white">{kind}</span>
    </label>
  );
};

const ItemRow = ({ it, selected, onSelect, onToggle, onChange }) => (
  <li className={`p-3 rounded-xl border ${it.status === "feito" ? "bg-emerald-50/60 border-emerald-200" : "bg-white border-slate-200"}`} data-testid={`fdpl-item-${it.id}`}>
    <div className="flex items-start gap-2">
      <input type="checkbox" checked={selected} onChange={() => onSelect(it.id)} className="mt-1.5 w-4 h-4 accent-violet-600" data-testid={`fdpl-select-${it.id}`} />
      <button type="button" onClick={() => onToggle(it)} className="shrink-0 mt-0.5" data-testid={`fdpl-check-${it.id}`} aria-label="Concluir">
        {it.status === "feito" ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <Circle className="w-6 h-6 text-slate-300" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] text-slate-800 whitespace-pre-wrap break-words">
          {it.priority === "urgente" && <Zap className="w-3.5 h-3.5 inline text-rose-600 mr-0.5" />}
          {it.quantity > 1 && <strong>{it.quantity}× </strong>}{it.description}
        </p>
        <p className="text-[10.5px] text-slate-500">
          Pedido em {formatDateBR(it.created_at?.slice(0, 10))}
          {it.scheduled_date && <> · agendado {formatDateBR(it.scheduled_date)}</>}
          {it.completed_at && <> · feito {formatDateBR(it.completed_at)}</>}
        </p>
      </div>
    </div>
    <div className="mt-2 pl-12 flex gap-2">
      {it.school_photo_path && (
        <a href={src(it.school_photo_path)} target="_blank" rel="noreferrer" className="relative w-14 h-14 rounded-lg overflow-hidden border border-sky-200 shrink-0">
          <img src={src(it.school_photo_path)} alt="" className="w-full h-full object-cover" />
          <span className="absolute bottom-0 inset-x-0 text-[8px] font-black uppercase text-center bg-sky-700/80 text-white">escola</span>
        </a>
      )}
      <PhotoPick item={it} kind="antes" onChange={onChange} />
      <PhotoPick item={it} kind="depois" onChange={onChange} />
    </div>
  </li>
);

const SchoolList = ({ name, items, reload }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [rows, setRows] = useState(items);
  const [date, setDate] = useState("");
  useEffect(() => { setRows(items); }, [items]);
  const pend = rows.filter((i) => i.status === "pendente");
  const done = rows.filter((i) => i.status === "feito");
  const selDone = done.filter((i) => sel.has(i.id)).map((i) => i.id);
  const selPend = pend.filter((i) => sel.has(i.id)).map((i) => i.id);
  const replace = (u) => setRows((p) => p.map((x) => (x.id === u.id ? u : x)));
  const toggleSel = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggle = async (it) => {
    try {
      const r = await api.patch(`/fdp-list/${it.id}`, { completed: it.status !== "feito" });
      replace(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const convert = async () => {
    if (!window.confirm(`Gerar orçamento FDP com ${selDone.length} item(ns) concluído(s)? Se a escola já tiver um FDP em aberto, os itens entram nele. Depois é só preencher os valores.`)) return;
    try {
      const r = await api.post("/fdp-list/convert", { item_ids: selDone });
      toast.success(r.data.appended ? `Itens adicionados ao orçamento FDP Nº ${r.data.number}` : `Orçamento Nº ${r.data.number} criado`);
      nav(`/orcamentos/${r.data.budget_id}/editar`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao gerar orçamento"); }
  };
  const schedule = async () => {
    try {
      await api.post("/fdp-list/schedule", { item_ids: selPend, date });
      toast.success("Agendado no cronograma");
      setSel(new Set()); setDate(""); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao agendar"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-violet-100 card-shadow" data-testid={`fdpl-school-${items[0]?.school_id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full p-4 flex items-center gap-3 text-left">
        <ListChecks className="w-5 h-5 text-violet-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-900 truncate">{name}</p>
          <p className="text-[11px] text-slate-500">{pend.length} pendente(s) · {done.length} feito(s)</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <ul className="space-y-2">
            {[...pend, ...done].map((it) => (
              <ItemRow key={it.id} it={it} selected={sel.has(it.id)} onSelect={toggleSel} onToggle={toggle} onChange={replace} />
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" data-testid="fdpl-schedule-date" />
            <Button size="sm" variant="outline" disabled={!selPend.length || !date} onClick={schedule} data-testid="fdpl-schedule-btn">
              <CalendarPlus className="w-4 h-4 mr-1.5" /> Agendar {selPend.length || ""} pendente(s)
            </Button>
            <Button size="sm" disabled={!selDone.length} onClick={convert} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="fdpl-convert-btn">
              <FileText className="w-4 h-4 mr-1.5" /> Gerar orçamento ({selDone.length})
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function FdpListsPanel() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/fdp-list").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const groups = useMemo(() => {
    const g = {};
    (items || []).forEach((i) => { (g[i.school_id] = g[i.school_id] || { name: i.school_name, items: [] }).items.push(i); });
    return Object.values(g).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);
  if (!items || !groups.length) return null;
  return (
    <section className="space-y-2" data-testid="fdp-lists-panel">
      <h2 className="text-sm font-black uppercase tracking-wider text-violet-700">Listas FDP das escolas</h2>
      {groups.map((g) => <SchoolList key={g.items[0].school_id} name={g.name} items={g.items} reload={load} />)}
    </section>
  );
}
